import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Models.css';
import { useModelsForHome } from '../context/HomeConfigContext.jsx';

function Models() {
  const models = useModelsForHome();
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const autoScrollIntervalRef = useRef(null);

  const openLightbox = (model) => {
    setLightbox(model);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightbox(null);
    document.body.style.overflow = '';
  };

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') closeLightbox();
    };
    if (lightbox) window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [lightbox]);

  const checkScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector('.tm-model-card')?.offsetWidth || 400;
      const gap = 24; // 1.5rem = 24px
      scrollRef.current.scrollBy({
        left: -(cardWidth + gap),
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const cardWidth = scrollRef.current.querySelector('.tm-model-card')?.offsetWidth || 400;
      const gap = 24; // 1.5rem = 24px
      scrollRef.current.scrollBy({
        left: cardWidth + gap,
        behavior: 'smooth',
      });
    }
  };

  // Auto-scroll functionality
  useEffect(() => {
    const startAutoScroll = () => {
      if (scrollRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            const cardWidth = scrollRef.current.querySelector('.tm-model-card')?.offsetWidth || 400;
            const gap = 24;
            const scrollAmount = cardWidth + gap;

            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 20;
            if (isAtEnd) {
              scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              // Continuar scrolleando
              scrollRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
              });
            }
          }
        }, 4000); // Auto-scroll cada 4 segundos
      }
    };

    startAutoScroll();

    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  // Pausar auto-scroll al hacer hover
  const handleMouseEnter = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    const startAutoScroll = () => {
      if (scrollRef.current) {
        autoScrollIntervalRef.current = setInterval(() => {
          if (scrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
            const cardWidth = scrollRef.current.querySelector('.tm-model-card')?.offsetWidth || 400;
            const gap = 24;
            const scrollAmount = cardWidth + gap;

            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 20;
            if (isAtEnd) {
              scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              scrollRef.current.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
              });
            }
          }
        }, 4000);
      }
    };
    startAutoScroll();
  };

  useEffect(() => {
    const scrollEl = scrollRef.current;
    checkScrollButtons();
    if (scrollEl) {
      scrollEl.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
    }
    return () => {
      if (scrollEl) {
        scrollEl.removeEventListener('scroll', checkScrollButtons);
      }
      window.removeEventListener('resize', checkScrollButtons);
    };
  }, []);

  return (
    <section id="modelos" className="tm-section tm-models-section">
      <div className="tm-container">
        <div className="tm-models-header">
          <div>
            <h3 className="tm-models-community">Nuestra Comunidad</h3>
            <h2 className="tm-models-title">
              Nuestras
              <span className="tm-models-title-italic">modelos</span>
            </h2>
          </div>
          <Link to="/galeria" className="tm-models-cta">
            Ver Galería Completa
            <span className="material-symbols-outlined tm-models-cta-icon">arrow_forward</span>
          </Link>
        </div>
        <div 
          className="tm-models-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <button
            type="button"
            className="tm-models-nav-btn tm-models-nav-btn-left"
            onClick={scrollLeft}
            disabled={!canScrollLeft}
            aria-label="Anterior"
          >
            <span className="material-symbols-outlined tm-models-nav-btn-icon">chevron_left</span>
          </button>
          <div className="tm-models-scroll-wrapper">
            <div
              ref={scrollRef}
              className="tm-models-scroll"
            >
              {models.map((model) => (
                <button
                  key={model.id}
                  type="button"
                  className="tm-model-card tm-model-card-btn"
                  onClick={() => openLightbox(model)}
                  aria-label={`Ver ${model.name} en grande`}
                >
                  <img
                    src={model.src}
                    alt={model.name}
                    className="tm-model-img"
                  />
                  <div className="tm-model-overlay" aria-hidden="true" />
                  <div className="tm-model-info">
                    <p className="tm-model-tag">{model.tag}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="tm-models-nav-btn tm-models-nav-btn-right"
            onClick={scrollRight}
            disabled={!canScrollRight}
            aria-label="Siguiente"
          >
            <span className="material-symbols-outlined tm-models-nav-btn-icon">chevron_right</span>
          </button>
        </div>
      </div>

      {lightbox && (
        <div
          className="tm-models-lightbox-overlay"
          onClick={closeLightbox}
          role="presentation"
        >
          <div
            className="tm-models-lightbox"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label={`Imagen de ${lightbox.name}`}
          >
            <button
              type="button"
              className="tm-models-lightbox-close"
              onClick={closeLightbox}
              aria-label="Cerrar"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
            <div className="tm-models-lightbox-image-wrap">
              <img
                src={lightbox.src}
                alt={lightbox.name}
                className="tm-models-lightbox-image"
              />
            </div>
            <div className="tm-models-lightbox-info">
              <p className="tm-models-lightbox-tag">{lightbox.tag}</p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Models;

